/* eslint-disable sonarjs/cognitive-complexity */
import { Injectable, OnDestroy, Inject } from '@angular/core';

import {
  catchError,
  filter,
  map,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom,
  takeUntil,
} from 'rxjs/operators';
import { BehaviorSubject, of, Subject, throwError } from 'rxjs';
import jwt_decode from 'jwt-decode';
import { AppState, AuthService } from '@auth0/auth0-angular';
import {
  UserFacade,
  UserProfile,
} from '@cigna/chcp/shared/user-profile-data-access';

import {
  ENVIRONMENT_CONFIG,
  EnvironmentConfig,
  APP_CONTEXT,
} from '@cigna/chcp/shared/environment-util';
import { AppContext } from '@cigna/chcp/shared/util';
import { Router } from '@angular/router';
import { WindowService } from '@cigna/shared/angular/core/window-util';
import { AuthFacade as CignaAuthFacade } from '@cigna/shared/angular/auth-data-access';
import { AuthFacade } from '@cigna/chcp/auth/data-access';

import { AuthError, OidcResult } from '@cigna/shared/angular/auth/oidc-util';
import { CookieService } from 'ngx-cookie-service';
import { FeatureTogglesFacade } from '@cigna/shared/angular/features-feature';
import { ChcpLaunchDarklyFlagKey } from '@cigna/chcp/shared/launch-darkly-util';

@Injectable()
export class RouteGuard implements OnDestroy {
  onDestroy$ = new Subject<void>();

  loader$ = new BehaviorSubject<boolean>(true);

  appState$ = this.auth.appState$.pipe(startWith(undefined));
  isImpersonator$ = this.authFacade.isImpersonator$;

  isImpersonator = false;

  constructor(
    public auth: AuthService,
    private authFacade: AuthFacade,
    private cignaAuthFacade: CignaAuthFacade,
    private userFacade: UserFacade,
    private router: Router,
    private windowService: WindowService,
    private cookieService: CookieService,
    private featureTogglesFacade: FeatureTogglesFacade,
    @Inject(APP_CONTEXT) public appContext: AppContext,
    @Inject(ENVIRONMENT_CONFIG) private envConfig: EnvironmentConfig,
  ) {
    this.isImpersonator$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((isImpersonating) => {
        this.isImpersonator = isImpersonating;
      });
  }

  authLoaded$ = this.auth.isAuthenticated$.pipe(
    switchMap((isAuth) => {
      this.loader$.next(true);

      if (this.envConfig.apiMock) {
        return of(this.authFacade.loadMockTokens());
      }

      if (!isAuth) {
        this.setDeepLink();
        return throwError(() => new AuthError({ error: 'login_required' }));
      }
      return this.auth.getAccessTokenSilently({
        ...(this.envConfig.auth0.hasAuthParamChange && {
          authorizationParams: {
            audience: this.envConfig.auth0.audience,
          },
        }),
      });
    }),
    map((token) => {
      if (this.envConfig.apiMock) {
        this.cignaAuthFacade.loadAuthSuccess(token as OidcResult);
      } else {
        this.cignaAuthFacade.loadAuthSuccess(tokenToOidc(token as string));
        this.setCookieStorage(token as string);
      }

      return true;
    }),
    catchError((e) => {
      this.cignaAuthFacade.loadAuthError(
        e instanceof AuthError ? e : new AuthError(e),
      );
      return of(false);
    }),
  );

  canActivateChild() {
    return this.authLoaded$.pipe(
      filter((auth) => auth),
      take(1),
      switchMap(() =>
        this.userFacade.userProfile$.pipe(
          tap((profile) => {
            if (profile === undefined || profile === null) {
              this.userFacade.getLoginProfile();
            }
          }),
          filter(
            (profile): profile is UserProfile =>
              profile !== undefined && profile !== null,
          ),
          take(1),
          withLatestFrom(
            this.featureTogglesFacade.featuresEnabled<ChcpLaunchDarklyFlagKey>([
              'ftr-chcp-pulsar-team-web-epp-wam-agreement-flow',
            ]),
            this.appState$,
          ),
          map(([userProfile, eppWamAgreementFlowEnabled, appState]) => {
            const deeplinkPath =
              this.windowService.sessionStorage.getItem('referrerPath');
            const LOGIN_PATH = '/app/login';
            if (this.isVicUser(appState)) {
              this.windowService.location.href = '/vic';
              return false;
            }

            // LOB deep link redirect OR Preference redirect
            if (this.isLobPrefRedirect(userProfile)) {
              const path = deeplinkPath
                ? deeplinkPath
                : this.windowService.location.pathname === LOGIN_PATH
                ? '/app/dashboard'
                : this.windowService.location.pathname;

              this.windowService.location.href = `${this.envConfig.redirect.crossOrigin.domain}/app/redirect?destinationUrl=${path}`;
              return false;
            }

            // eft lockout
            if (this.isEftLockout(userProfile)) {
              this.router.navigate(['/login'], {
                queryParams: {
                  status: 'deactive',
                  expireTs: `${userProfile.inayrib}`,
                },
              });
              return false;
            }

            // deny
            if (this.isDeny(userProfile)) {
              this.userFacade.updateWatchAudit();
              this.router.navigate(['/login'], {
                queryParams: { status: 'deny' },
              });
              return false;
            }

            // no wam
            if (this.isNoWam(userProfile)) {
              this.router.navigate(['/login'], {
                queryParams: { status: 'nowam' },
              });
              return false;
            }

            // wam error
            if (this.isWamError(userProfile)) {
              this.router.navigate(['/login'], {
                queryParams: { status: 'wamerror' },
              });
              return false;
            }

            // needs agreement
            if (this.isAgreement(userProfile) && !this.isImpersonator) {
              if (
                !deeplinkPath &&
                this.windowService.location.pathname !== LOGIN_PATH
              ) {
                this.setDeepLink();
              }
              this.router.navigate([`/agreement`]);
              return false;
            }

            // needs wam agreement
            if (
              this.isWamAgreement(userProfile, eppWamAgreementFlowEnabled) &&
              !this.isImpersonator
            ) {
              if (
                !deeplinkPath &&
                this.windowService.location.pathname !== LOGIN_PATH
              ) {
                this.setDeepLink();
              }
              this.router.navigate([`/agreement/wam`]);
              return false;
            }

            this.loader$.next(false);

            // eft enable
            if (this.shouldUnlockEft(userProfile)) {
              return true;
            }

            if (
              !this.isAgreement(userProfile) &&
              !this.isWamAgreement(userProfile, eppWamAgreementFlowEnabled) &&
              deeplinkPath &&
              ((userProfile.lobPref === 'CHCP' && this.appContext === 'chcp') ||
                (userProfile.lobPref === 'EBH' &&
                  this.appContext === 'evernorth'))
            ) {
              this.windowService.sessionStorage.removeItem('referrerPath');
            }

            return true;
          }),
        ),
      ),
    );
  }

  isEftLockout(profile: UserProfile): boolean {
    if (profile.inayrib) {
      const expireTime = +profile.inayrib;
      const currentTime = new Date().getTime();

      return expireTime > currentTime;
    }
    return false;
  }

  shouldUnlockEft(profile: UserProfile): boolean {
    if (profile.inayrib) {
      const expireTime = +profile.inayrib;
      const currentTime = new Date().getTime();

      return expireTime < currentTime;
    }
    return false;
  }

  isDeny(profile: UserProfile): boolean {
    return profile.status === 'deny' && profile.allowAccess === false;
  }

  isNoWam(profile: UserProfile): boolean {
    return profile.status === 'nowam' && profile.allowAccess === false;
  }

  isWamError(profile: UserProfile): boolean {
    return profile.status === 'wamerror' && profile.allowAccess === false;
  }

  isWamAgreement(
    profile: UserProfile,
    eppWamAgreementFlowEnabled = false,
  ): boolean {
    if (!profile.allEntitlements?.includes('DelegationAccessAndViews')) {
      return false;
    }
    if (this.appContext === 'chcp') {
      return profile.chcpWamAgreed === 'N';
    }
    if (this.appContext === 'evernorth') {
      return eppWamAgreementFlowEnabled && profile.eppWamAgreed === 'N';
    }
    return false;
  }

  isAgreement(profile: UserProfile): boolean {
    return (
      (profile.chcpAppAgreed === 'N' && this.appContext === 'chcp') ||
      (profile.eppAppAgreed === 'N' && this.appContext === 'evernorth')
    );
  }

  isLobPrefRedirect(profile: UserProfile): boolean {
    return (
      (profile.lobPref === 'CHCP' && this.appContext === 'evernorth') ||
      (profile.lobPref === 'EBH' && this.appContext === 'chcp')
    );
  }

  isVicUser(appState?: AppState): boolean {
    return appState?.loginContext === 'vic';
  }

  stopLoader() {
    this.loader$.next(false);
  }

  // once we retire isam app we can reference this directly from auth0 service
  // import { Auth0FlowService } from '@cigna/shared/angular/auth/auth0-util';
  setCookieStorage(token: string) {
    if (this.envConfig.auth0.cookieStorage) {
      this.cookieService.set(
        'accessToken',
        token,
        new Date(Date.now() + 10 * 60 * 1000), // +10 minutes
        '/',
        undefined, // domain
        true, // secure
      );
    }
  }

  setDeepLink() {
    this.windowService.sessionStorage.referrerPath =
      this.windowService.location.pathname + this.windowService.location.search;
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
    this.loader$.next(false);
  }
}

function tokenToOidc(token: string): OidcResult {
  const accessTokenBody = jwt_decode(token);
  return {
    accessToken: token,
    accessTokenBody,
    idTokenBody: {},
  } as unknown as OidcResult;
}
